/**
 * Asset-URL ↔ S3-key helpers.
 *
 * Asset URLs come in two shapes; both must reduce to the raw S3 key
 * (`<eventId>/<file>` or `shared/...`) that the backend's
 * copy-to-title-image / delete-images endpoints validate against:
 *   prod (vhost-style):    https://assets.veltric.app/<key>
 *   dev (LocalStack path): http://localhost:4566/public-assets/<key>
 *
 * Extracted from PictureInputPopup (BUGS.md M20, 2026-07-05 W2-E): pickers
 * now store the FULL asset URL in configs (previews sanitize with an
 * absolute-https check), so backend-facing callers normalize URL → key here.
 */
export function extractAssetKey(url: string): string {
  try {
    const parsed = new URL(url);
    let path = parsed.pathname.startsWith("/")
      ? parsed.pathname.substring(1)
      : parsed.pathname;
    if (path.startsWith("public-assets/")) {
      path = path.substring("public-assets/".length);
    }
    return path;
  } catch {
    // Not a URL — already a bare key (legacy configs).
    return url;
  }
}

export function extractFileName(assetKey: string): string {
  const lastSlash = assetKey.lastIndexOf("/");
  return lastSlash >= 0 ? assetKey.substring(lastSlash + 1) : assetKey;
}

/**
 * Append a query parameter to an asset URL.
 *
 * Exists because the asset host's CORS answer is CACHED PER URL, and the same
 * object is requested two ways: the tile paints it through an `<img>`, which
 * sends NO `Origin` and gets an answer carrying neither
 * `Access-Control-Allow-Origin` nor `Vary` — and a response without `Vary` can
 * legitimately be replayed to a later request that DOES send `Origin`. That
 * later request is our download `fetch`, and without the ACAO header it fails
 * the CORS check while the very same image renders perfectly beside it.
 *
 * Giving the CORS-mode fetch its own query string gives it its own cache key,
 * one that only ever sees `Origin`-bearing requests and therefore cannot
 * inherit the `<img>`'s header-less answer. Verified against the live host: the
 * query string IS part of the cache key, and the decorated URL answers with
 * `Access-Control-Allow-Origin` + `Vary: Origin`.
 *
 * Uses `URL` rather than string concat because asset URLs may ALREADY carry a
 * query (`?v=<version>` cache-busting is minted in several places, e.g.
 * pages/builders/app/appTitleImage.ts), so the separator is not always `?`.
 * Non-URL input (legacy bare keys, same case `extractAssetKey` guards above)
 * is returned untouched — there is nothing to decorate.
 */
export function withAssetParam(url: string, key: string, value: string): string {
  try {
    const parsed = new URL(url);
    parsed.searchParams.set(key, value);
    return parsed.toString();
  } catch {
    return url;
  }
}
